import classNames from "classnames";
import React, { FC, useEffect, useRef } from "react";
import { useMedia } from "react-use";

import { ITestable, testAttr } from "@/browser";
import { Button } from "@/components/Button";
import { Section } from "@/components/Section";
import { MEDIA_SIZE_SM } from "@/core/media";
import {
  DropdownComponent as EworkitDropdown,
  IDropdownProps as IEworkitDropdownProps,
  IDropdownStateProps,
  withDropdownState,
} from "@/eworkitComponents";

export interface IActionsRenderProps extends ITestable {
  open?: boolean;
}

export interface IActionsProps extends Partial<IEworkitDropdownProps>, IDropdownStateProps, ITestable {
  className?: string;
  renderTarget?: (props: IActionsRenderProps) => JSX.Element;
  onOpen?: () => void;
  onClose?: () => void;
  renderActiveBar?: boolean;
}

const ActionsComponent: FC<IActionsProps> = props => {
  const { renderTarget, open, onTargetClick, renderActiveBar = true } = props;

  const isSmallScreen = useMedia(MEDIA_SIZE_SM);

  const handleContentClick = () => {
    if (onTargetClick) {
      onTargetClick();
    }
  };

  const renderDefaultTarget = () => {
    return <Button variant="clean" icon="more" size="md" testName="more" />;
  };

  const renderTargetOrDefault = () => {
    return (
      <div className="actions-target" {...testAttr(props, { sufix: "actions-target" })}>
        {renderTarget ? renderTarget({ open }) : renderDefaultTarget()}
        {!!open && renderActiveBar && <div className="actions-target-active-bar" />}
      </div>
    );
  };

  const renderContent = () => {
    return (
      <Section className="actions-content" {...testAttr(props, { sufix: "actions-content" })}>
        <div onClick={handleContentClick}>{props.children}</div>
      </Section>
    );
  };

  const { className, children, ...dropdownProps } = props;

  const classes = classNames(
    "actions",
    {
      [`actions--open`]: !!open,
    },
    className,
  );

  const prevOpen = useRef(open);

  useEffect(() => {
    if (!prevOpen.current && open && props.onOpen) {
      props.onOpen();
    }

    if (prevOpen.current && !open && props.onClose) {
      props.onClose();
    }

    prevOpen.current = open;
  }, [open]);

  return (
    <EworkitDropdown
      className={classes}
      target={renderTargetOrDefault()}
      content={renderContent()}
      placement="bottom-end"
      open={open}
      positionFixed
      modifiers={{
        preventOverflow: isSmallScreen
          ? {
              padding: 0,
            }
          : undefined,
      }}
      {...dropdownProps}
    />
  );
};

export const Actions = withDropdownState(ActionsComponent);
